import React, { createContext, useReducer, useContext, ReactNode } from 'react'
import { AppVariant } from 'src/lib'

import { Config } from '@customTypes/Config'

type SetAfterBookingState = {
  type: 'setAfterBookingState'
  payload: AfterBookingState
}

type SetClinicId = {
  type: 'setClinicId'
  payload: string
}

type SetVariant = {
  type: 'setVariant'
  payload: AppVariant
}

type SetOriginalClinicId = {
  type: 'setOriginalClinicId'
  payload: InitialState['originalClinicId']
}

type SetIsInternalClinic = {
  type: 'setIsInternalClinic'
  payload: boolean
}

type SetClinicLogoUrl = {
  type: 'setClinicLogoUrl'
  payload: string | undefined
}

type Action =
  | SetClinicId
  | SetVariant
  | SetAfterBookingState
  | SetOriginalClinicId
  | SetIsInternalClinic
  | SetClinicLogoUrl

type Dispatch = (action: Action) => void
interface InitialState extends Config {
  variant?: AppVariant
  originalClinicId?: string
  isInternalClinic?: boolean
  clinicLogoUrl?: string
}

export interface AfterBookingState {
  hasHealthAssistant: boolean
  hasValidAnamnesis: boolean
  healthAssistantLink?: string
  linkToAna?: string
}

export interface ConfigState extends InitialState, AfterBookingState {}

export interface ConfigProviderProps {
  children: ReactNode
  initialConfig: InitialState
}

const changable = {
  hasHealthAssistant: false,
  hasValidAnamnesis: false,
  healthAssistantLink: '',
  linkToAna: '',
  originalClinicId: undefined,
}

const ConfigContext = createContext<ConfigState | undefined>(undefined)
const ConfigDispatchContext = createContext<Dispatch | undefined>(undefined)

const configReducer = (state: ConfigState, action: Action): ConfigState => {
  switch (action.type) {
    case 'setAfterBookingState':
      return { ...state, ...action.payload }
    case 'setClinicId':
      return { ...state, clinicId: action.payload }
    case 'setVariant':
      return { ...state, variant: action.payload }
    case 'setOriginalClinicId':
      return { ...state, originalClinicId: action.payload }
    case 'setIsInternalClinic':
      return { ...state, isInternalClinic: action.payload }
    case 'setClinicLogoUrl':
      return { ...state, clinicLogoUrl: action.payload }
  }
}

export const ConfigProvider = ({ children, initialConfig }: ConfigProviderProps) => {
  const [config, dispatch] = useReducer(configReducer, { ...changable, ...initialConfig })
  return (
    <ConfigContext.Provider value={config}>
      <ConfigDispatchContext.Provider value={dispatch}>{children}</ConfigDispatchContext.Provider>
    </ConfigContext.Provider>
  )
}

export const useConfig = () => {
  const context = useContext(ConfigContext)
  if (context === undefined) {
    throw new Error('useConfig must be user within a ConfigProvider')
  }
  return context
}

export const useConfigDispatch = () => {
  const context = useContext(ConfigDispatchContext)
  if (context === undefined) {
    throw new Error('useConfigDispatch must be used within a ConfigProvider')
  }
  return context
}

export const setAfterBookingData = (payload: AfterBookingState): SetAfterBookingState => ({
  type: 'setAfterBookingState',
  payload,
})
export const setClinicId = (payload: SetClinicId['payload']): SetClinicId => ({ type: 'setClinicId', payload })
export const setVariant = (payload: SetVariant['payload']): SetVariant => ({
  type: 'setVariant',
  payload,
})

export const setOriginalClinicId = (payload: InitialState['originalClinicId']): SetOriginalClinicId => ({
  type: 'setOriginalClinicId',
  payload,
})

export const setIsInternalClinic = (payload: boolean): SetIsInternalClinic => ({
  type: 'setIsInternalClinic',
  payload,
})

export const setClinicLogoUrl = (payload: string | undefined): SetClinicLogoUrl => ({
  type: 'setClinicLogoUrl',
  payload,
})
